import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { Button } from "@/shared/ui/button";
import { Settings2, Check, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { DELIVERABLES, DELIVERABLE_CONFIG, type DeliverableKey } from "@/features/properties/services/deliveryStatusConfig";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/shared/ui/dropdown-menu";
import {
  generatePropertyReport,
  PROPERTY_REPORT_TEMPLATES,
  type PropertyReportTemplate,
} from "@/features/exports/services/propertyReport";
import {
  buildPropertyPlan,
  applyPlans,
  type PropertyPlan,
} from "@/features/properties/services/deliveryStateRecompute";
import { RecomputeReviewDialog } from "./RecomputeReviewDialog";

type PropertyFieldsEditorProps = {
  propertyId: string;
};

const db = supabase as unknown as any;

const PropertyFieldsEditor = ({ propertyId }: PropertyFieldsEditorProps) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["property-fields", propertyId],
    queryFn: async () => {
      const { data, error } = await db
        .from("properties")
        .select("*")
        .eq("id", propertyId)
        .single();
      if (error) {
        console.error("[PropertyFieldsEditor] Failed to load property:", error);
        throw error;
      }
      return data;
    },
    enabled: !!propertyId,
  });

  const { data: latestSiteVisitDate } = useQuery({
    queryKey: ["property-latest-site-visit-date", propertyId],
    queryFn: async () => {
      const { data, error } = await db
        .from("property_attributes")
        .select("captured_at")
        .eq("property_id", propertyId)
        .order("captured_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.captured_at ?? null;
    },
    enabled: !!propertyId,
  });

  const [status, setStatus] = useState<string>("");
  const [squareFootage, setSquareFootage] = useState<string>("");
  const [climateZone, setClimateZone] = useState<string>("");
  const [hvacTonnage, setHvacTonnage] = useState<string>("");
  const [electricalPanel, setElectricalPanel] = useState<string>("");
  const [generatingReport, setGeneratingReport] = useState(false);
  const [recomputePlans, setRecomputePlans] = useState<PropertyPlan[] | null>(null);
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [buildingPlan, setBuildingPlan] = useState(false);
  const [applyingPlan, setApplyingPlan] = useState(false);

  const handleRecompute = async () => {
    if (!propertyId || buildingPlan) return;
    setBuildingPlan(true);
    try {
      const plan = await buildPropertyPlan(propertyId);
      setRecomputePlans([plan]);
      setRecomputeOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to compute changes";
      toast.error(message);
    } finally {
      setBuildingPlan(false);
    }
  };

  const handleApplyRecompute = async () => {
    if (!recomputePlans) return;
    setApplyingPlan(true);
    try {
      const { updated, failed } = await applyPlans(recomputePlans);
      if (failed > 0) toast.error(`Updated ${updated}, ${failed} failed`);
      else if (updated > 0) toast.success("Property status updated");
      else toast.info("No changes to apply");
      queryClient.invalidateQueries({ queryKey: ["property-fields", propertyId] });
      queryClient.invalidateQueries({
        queryKey: ["property-latest-site-visit-date", propertyId],
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to apply changes";
      toast.error(message);
    } finally {
      setApplyingPlan(false);
      setRecomputeOpen(false);
      setRecomputePlans(null);
    }
  };

  const handleGenerateReport = async (template: PropertyReportTemplate) => {
    if (!propertyId || generatingReport) return;
    setGeneratingReport(true);
    try {
      await generatePropertyReport(propertyId, template, data?.name);
      toast.success("Report downloaded");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to generate report";
      console.error("Generate property report failed:", err);
      toast.error(message);
    } finally {
      setGeneratingReport(false);
    }
  };

  useEffect(() => {
    if (data) {
      setStatus(data.delivery_status ?? "");
      setSquareFootage(data.square_footage?.toString() ?? "");
      setClimateZone(data.climate_zone ?? "");
      setHvacTonnage(data.hvac_tonnage?.toString() ?? "");
      setElectricalPanel(data.electrical_panel ?? "");
    }
  }, [data]);

  const updateField = useMutation({
    mutationFn: async ({
      column,
      value,
    }: {
      column: string;
      value: string | boolean | null;
    }) => {
      const { error } = await db
        .from("properties")
        .update({ [column]: value })
        .eq("id", propertyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-fields", propertyId] });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Failed to save field";
      toast.error(message);
    },
  });

  const handleSave = (column: string, value: string) => {
    const trimmed = value.trim();
    updateField.mutate({ column, value: trimmed || null });
  };

  const formatSiteVisitDate = (value?: string | null) => {
    if (!value) return "No site visit recorded";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "No site visit recorded";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Property Details</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecompute}
              disabled={buildingPlan}
              className="gap-1.5"
            >
              {buildingPlan ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Checking…
                </>
              ) : (
                <>
                  <RefreshCw className="h-3.5 w-3.5" />
                  Recompute status
                </>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  disabled={generatingReport}
                  className="gap-1.5 shadow-sm"
                >
                  {generatingReport ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <FileText className="h-3.5 w-3.5" />
                      Generate Report
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {PROPERTY_REPORT_TEMPLATES.map((t) => (
                  <DropdownMenuItem
                    key={t.value}
                    onSelect={() => handleGenerateReport(t.value)}
                  >
                    {t.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Status */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select
              value={status}
              onValueChange={(val) => {
                setStatus(val);
                handleSave("delivery_status", val);
              }}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="survey_scheduled">Survey Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Site Visit Date */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Site Visit Date</Label>
            <Input
              className="text-sm"
              value={formatSiteVisitDate(latestSiteVisitDate)}
              readOnly
            />
          </div>


          {/* Square Footage */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">SQ FT</Label>
            <Input
              className="text-sm"
              value={squareFootage}
              onChange={(e) => setSquareFootage(e.target.value)}
              onBlur={() => handleSave("square_footage", squareFootage)}
            />
          </div>

          {/* Climate Zone */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Climate Zone</Label>
            <Select
              value={climateZone}
              onValueChange={(val) => {
                const value = val === "__none__" ? "" : val;
                setClimateZone(value);
                handleSave("climate_zone", value);
              }}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Select climate zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="text-muted-foreground italic">— None —</span></SelectItem>
                <SelectItem value="0A - Extremely Hot – Humid (equatorial rainforest)">0A - Extremely Hot – Humid (equatorial rainforest)</SelectItem>
                <SelectItem value="0B - Extremely Hot – Dry (extreme desert interiors)">0B - Extremely Hot – Dry (extreme desert interiors)</SelectItem>
                <SelectItem value="1A - Very Hot – Humid (tropical climates like southern Florida, Caribbean)">1A - Very Hot – Humid (southern Florida, Caribbean)</SelectItem>
                <SelectItem value="1B - Very Hot – Dry (extreme desert)">1B - Very Hot – Dry (extreme desert)</SelectItem>
                <SelectItem value="2A - Hot – Humid (South Florida, Gulf Coast)">2A - Hot – Humid (South Florida, Gulf Coast)</SelectItem>
                <SelectItem value="2B - Hot – Dry (desert Southwest)">2B - Hot – Dry (desert Southwest)</SelectItem>
                <SelectItem value="3A - Warm – Humid (Southeast, Gulf states)">3A - Warm – Humid (Southeast, Gulf states)</SelectItem>
                <SelectItem value="3B - Warm – Dry (Arizona, inland Southern California)">3B - Warm – Dry (Arizona, inland Southern California)</SelectItem>
                <SelectItem value="3C - Warm – Marine (coastal Southern California)">3C - Warm – Marine (coastal Southern California)</SelectItem>
                <SelectItem value="4A - Mixed – Humid (Mid-Atlantic, lower Midwest)">4A - Mixed – Humid (Mid-Atlantic, lower Midwest)</SelectItem>
                <SelectItem value="4B - Mixed – Dry (New Mexico, western Texas high desert)">4B - Mixed – Dry (New Mexico, western Texas)</SelectItem>
                <SelectItem value="4C - Mixed – Marine (coastal California, Oregon coast)">4C - Mixed – Marine (coastal California, Oregon coast)</SelectItem>
                <SelectItem value="5A - Cool – Humid (Midwest, Northeast)">5A - Cool – Humid (Midwest, Northeast)</SelectItem>
                <SelectItem value="5B - Cool – Dry (high plains, Utah/Colorado interior)">5B - Cool – Dry (high plains, Utah/Colorado)</SelectItem>
                <SelectItem value="5C - Cool – Marine (Pacific Northwest coastal)">5C - Cool – Marine (Pacific Northwest coastal)</SelectItem>
                <SelectItem value="6A - Cold – Humid (Great Lakes, New England interior)">6A - Cold – Humid (Great Lakes, New England)</SelectItem>
                <SelectItem value="6B - Cold – Dry (Rocky Mountains, interior Northwest)">6B - Cold – Dry (Rocky Mountains, interior Northwest)</SelectItem>
                <SelectItem value="7 - Very Cold (interior Alaska, northern Minnesota)">7 - Very Cold (interior Alaska, northern Minnesota)</SelectItem>
                <SelectItem value="8 - Subarctic / Arctic (e.g., northern Alaska, northern Canada)">8 - Subarctic / Arctic (northern Alaska, northern Canada)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* HVAC Tonnage */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">HVAC Tonnage</Label>
            <Input
              className="text-sm"
              value={hvacTonnage}
              onChange={(e) => setHvacTonnage(e.target.value)}
              onBlur={() => handleSave("hvac_tonnage", hvacTonnage)}
            />
          </div>

          {/* Electrical Panel */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Electrical Panel
            </Label>
            <Input
              className="text-sm"
              value={electricalPanel}
              onChange={(e) => setElectricalPanel(e.target.value)}
              onBlur={() => handleSave("electrical_panel", electricalPanel)}
            />
          </div>
        </div>

        {/* Deliverables */}
        <div className="mt-6 pt-4 border-t border-border/50">
          <Label className="text-xs text-muted-foreground mb-3 block">Deliverables</Label>
          <div className="flex flex-wrap gap-2">
            {DELIVERABLES.map((key) => {
              const cfg = DELIVERABLE_CONFIG[key as DeliverableKey];
              const isChecked = data?.[key] === true;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    const newVal = !isChecked;
                    updateField.mutate({ column: key, value: newVal });
                  }}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border",
                    isChecked
                      ? "bg-primary/15 border-primary/40 text-primary"
                      : "bg-muted/30 border-border/50 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                  )}
                >
                  <div
                    className={cn(
                      "h-3.5 w-3.5 rounded-full flex items-center justify-center transition-all",
                      isChecked
                        ? "bg-primary"
                        : "border border-muted-foreground/30"
                    )}
                  >
                    {isChecked && (
                      <Check className="h-2 w-2 text-primary-foreground" strokeWidth={3} />
                    )}
                  </div>
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>
        <RecomputeReviewDialog
          open={recomputeOpen}
          plans={recomputePlans ?? []}
          applying={applyingPlan}
          onConfirm={handleApplyRecompute}
          onCancel={() => {
            if (applyingPlan) return;
            setRecomputeOpen(false);
            setRecomputePlans(null);
          }}
        />
      </CardContent>
    </Card>
  );
};

export { PropertyFieldsEditor };
